import { GlobalHomePageRoute } from '@townsquare/router/route-types';
import { goalDirectoryRoute } from '@townsquare/routes/goal-directory-view';
import { projectDirectoryRoute } from '@townsquare/routes/project-directory-view';
import { tqlQueryFromInput } from '@townsquare/tql/query';
import { ComparatorOperator } from '@townsquare/tql/types';

import { DirectoryViewEntityType } from './components/__generated__/DirectoryFavoriteViewButton.graphql';
import { ViewTab } from './hooks/useSelectedView';
import { DirectoryRoute } from './types';

// Used for url parameter tql matching as a shortcut between the url path updating and explainTql loading.
// Allows for smoother view changes.
const getArchivedTql = (archived: boolean) =>
	tqlQueryFromInput({
		input: [
			{
				fieldName: 'archived',
				fieldValue: archived,
				comparator: ComparatorOperator.EQUALS,
			},
		],
	});

export const archivedTql: string = getArchivedTql(true);
export const unarchivedTql: string = getArchivedTql(false);

export const emptyTeamsTql: string = tqlQueryFromInput({
	input: [
		{
			fieldName: 'showEmptyTeams',
			fieldValue: true,
			comparator: ComparatorOperator.EQUALS,
		},
	],
});

export const yourItemsTql = (userId: string, entity: DirectoryRoute): string =>
	tqlQueryFromInput({
		input: [
			{
				fieldName: 'archived',
				fieldValue: false,
				comparator: ComparatorOperator.EQUALS,
			},
			{
				fieldName: entity === 'goals' ? 'owner' : 'contributor',
				fieldValue: userId,
				comparator: ComparatorOperator.EQUALS,
			},
		],
	});

export const yourTeamsTql = (userId: string): string =>
	tqlQueryFromInput({
		input: [
			{
				fieldName: 'showEmptyTeams',
				fieldValue: true,
				comparator: ComparatorOperator.EQUALS,
			},
			{
				fieldName: 'member',
				fieldValue: userId,
				comparator: ComparatorOperator.EQUALS,
			},
		],
	});

export const archivedTeamsTql = (): string =>
	tqlQueryFromInput({
		input: [
			{
				fieldName: 'showEmptyTeams',
				fieldValue: true,
				comparator: ComparatorOperator.EQUALS,
			},
			{
				fieldName: 'state',
				fieldValue: 'DISBANDED',
				comparator: ComparatorOperator.EQUALS,
			},
		],
	});

export const followingTql = (userId: string): string =>
	tqlQueryFromInput({
		input: [
			{
				fieldName: 'archived',
				fieldValue: false,
				comparator: ComparatorOperator.EQUALS,
			},
			{
				fieldName: 'watcher',
				fieldValue: userId,
				comparator: ComparatorOperator.EQUALS,
			},
		],
	});

export const entityTypeToDirectoryRoute = (
	entityType?: DirectoryViewEntityType | null,
): GlobalHomePageRoute => {
	if (entityType === 'GOAL') {
		return goalDirectoryRoute;
	}
	return projectDirectoryRoute;
};

export const getUrlForViewTab = (entity: DirectoryRoute, viewTab: ViewTab, tql: string): string => {
	const tqlEncoded = encodeURIComponent(tql);
	if (entity === 'teams') {
		return `/people/search?screen=SEARCH_TEAMS&tql=${tqlEncoded}&viewTab=${viewTab}`;
	}
	return `${entity}?tql=${tqlEncoded}&viewTab=${viewTab}`;
};

export const getAnalyticsContext = (
	entity: DirectoryRoute,
): 'people' | 'project' | 'team' | 'goal' => {
	switch (entity) {
		case 'projects':
			return 'project';
		case 'teams':
			return 'team';
		case 'people':
			return 'people';
		default:
			return 'goal';
	}
};
